import {
  ActivityType, AndroidHealthConnectExerciseTypeMapping, getMappedWorkoutActivityType, UserWorkoutSession,
} from 'fitbeat.gym-core';
import { Platform } from 'react-native';
import { Logger } from '../../utils/Logger';
import BaseNonRealTimeConnectedApp from '../base-non-real-time-connected-app';
import {
  AuthorizationStatus, IAuthorizationResult, INotOnboardable, IOnboardable,
} from '../connected-workout-app';
import HealthConnectWrapper from './HealthConnectWrapper';
import { showMessage } from '../../ui/components/Toast';

function getHealthConnectExericiseTypeId(activityType: ActivityType) {
  return getMappedWorkoutActivityType(
    activityType,
    AndroidHealthConnectExerciseTypeMapping,
    Logger.notify,
  );
}

class HealthConnect extends BaseNonRealTimeConnectedApp {
  private wrapper = HealthConnectWrapper;

  public async isOnboardable(): Promise<IOnboardable | INotOnboardable> {
    if (Platform.OS !== 'android') {
      return {
        isOnboardable: false,
      };
    }

    const availability = await this.wrapper.getAvailability();
    if (availability === 'Unavailable') {
      Logger.leaveBreadcrumb('[HealthConnect] is not available on this device');
      return {
        isOnboardable: false,
      };
    }

    return {
      isOnboardable: true,
      isAppInstalled: availability === 'Ready',
    };
  }

  public async isAvailable(): Promise<boolean> {
    // if it is onboardable, then it can be connected from the more menu (can start install when connecting)
    const onboardable = await this.isOnboardable();
    return onboardable.isOnboardable;
  }

  public initialize(): Promise<void> {
    return this.wrapper.initializeHealthConnect();
  }

  public async debugTestIntegration(): Promise<void> {
    try {
      const debugInfo = await this.wrapper.debug();
      Logger.debug(`[HealthConnect] debug info: ${debugInfo}`);
      showMessage(debugInfo);
    } catch (e) {
      Logger.leaveBreadcrumb(`[HealthConnect] failed to get debug info: ${e}`);
    }
  }

  public async requestAuthorization(): Promise<IAuthorizationResult> {
    try {
      const success = await this.wrapper.requestPermissions();
      return {
        success,
        errorDisplayMessage: success ? undefined : 'Not all permissions were granted',
      };
    } catch (e) {
      return {
        success: false,
        errorDisplayMessage: (e as Error).message || (e as string),
      };
    }
  }

  public async authorizationStatus(): Promise<AuthorizationStatus> {
    const allGranted = await this.wrapper.isAllPermissionsGranted();
    if (allGranted) {
      return AuthorizationStatus.authorized;
    }
    return AuthorizationStatus.denied;
  }

  public async stopAndSaveWorkout(workoutSession: UserWorkoutSession): Promise<void> {
    try {
      const healthConnectExerciseTypeId = getHealthConnectExericiseTypeId(workoutSession.settings.activityType);
      const request = {
        fitillionId: workoutSession.uniqueReference,
        healthConnectExerciseTypeId,
        startTimeMilliseconds: new Date(workoutSession.startTimeOfFirstExercise).getTime(),
        endTimeMilliseconds: new Date(workoutSession.endTime).getTime(),
      };
      Logger.debug(`[HealthConnect] Saving workout ${JSON.stringify(request)}`);

      const healthConnectId = await this.wrapper.exportWorkout(request);
      Logger.debug(`[HealthConnect] exported ${request.fitillionId} as ${healthConnectId}`);
    } catch (e) {
      Logger.notify(new Error(`[HealthConnect] Failed to save workout: ${(e as Error).message}`));
    }
  }
}

const healthConnect = new HealthConnect('Health Connect');

export default healthConnect;
